import { darken, lighten } from "@mui/material/styles";
import { isEmpty } from "lodash";

/**
 * Adjusts the brightness of a hex color using MUI's color utilities
 * @param hex - Hex color string (e.g., "#5C4EE5")
 * @param amount - Brightness adjustment (positive = lighter, negative = darker)
 *                 Value is normalized: 40 → 0.16 coefficient for MUI functions
 * @returns Adjusted hex color string
 */
export const adjustBrightness = (hex: string, amount: number): string => {
  // Normalize: original used 0-255 scale, MUI uses 0-1 coefficient
  // 40 in old scale ≈ 0.16 in MUI scale (40/255)
  const coefficient = Math.abs(amount) / 255;
  return amount > 0 ? lighten(hex, coefficient) : darken(hex, coefficient);
};

/**
 * Calculates relative luminance of a color (WCAG formula)
 * @param hex - Hex color string
 * @returns Luminance value between 0 (darkest) and 1 (lightest)
 */
export const getLuminance = (hex: string): number => {
  const rgb = hex
    .replace("#", "")
    .match(/.{2}/g)!
    .map((c) => {
      const val = parseInt(c, 16) / 255;
      return val <= 0.03928
        ? val / 12.92
        : Math.pow((val + 0.055) / 1.055, 2.4);
    });
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
};

/**
 * Determines if a background color is dark (needs light text)
 * @param hex - Background hex color
 * @returns true if dark background (luminance < 0.5)
 */
export const isDarkColor = (hex: string): boolean => getLuminance(hex) < 0.5;

/**
 * Generates sidebar colors that contrast with background
 * @param backgroundColor - Sidebar background hex color
 * @returns Object with text, selected background, and contrast text colors
 */
export const generateSidebarContrastColors = (
  backgroundColor: string,
): {
  textColor: string;
  selectedBgColor: string;
  contrastTextColor: string;
  subHeaderColor: string;
} => {
  const isDark = isDarkColor(backgroundColor);

  if (isDark) {
    // Dark background → light text
    return {
      textColor: "#ffffff",
      selectedBgColor: lighten(backgroundColor, 0.2),
      contrastTextColor: "#ffffff",
      subHeaderColor: "rgba(255, 255, 255, 0.7)",
    };
  } else {
    // Light background → dark text
    return {
      textColor: "#1D1D1D",
      selectedBgColor: darken(backgroundColor, 0.15),
      contrastTextColor: "#ffffff",
      subHeaderColor: "#2b2b2b",
    };
  }
};

/**
 * Generates a color based on the main color with adjustments
 * @param mainColor - The base color in hex format (e.g. "#1fadb2")
 * @param index - Index used to adjust the color
 * @returns A hex color code
 */
export function generateVariantColor(mainColor: string, index: number): string {
  // Parse the hex color to RGB
  const r = parseInt(mainColor.slice(1, 3), 16);
  const g = parseInt(mainColor.slice(3, 5), 16);
  const b = parseInt(mainColor.slice(5, 7), 16);

  // Apply adjustments based on index - shift more toward green/yellow variations
  const adjustedR = Math.min(255, Math.max(0, r + index * 15));
  const adjustedG = Math.min(255, Math.max(0, g + index * 5));
  const adjustedB = Math.min(255, Math.max(0, b - index * 10));

  // Convert back to hex
  return `#${adjustedR.toString(16).padStart(2, "0")}${adjustedG.toString(16).padStart(2, "0")}${adjustedB.toString(16).padStart(2, "0")}`;
}

/**
 * Gets a color based on an identifier
 * @param identifier - The identifier to generate a color for
 * @param mainColor - The base color
 */
export function getColorFromId(
  identifier: string,
  mainColor: string = "#1fadb2",
): string {
  // Use a numeric hash of the identifier to get a consistent index
  const numericId = !isEmpty(identifier)
    ? identifier.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      10
    : 0;

  return generateVariantColor(mainColor, numericId);
}
